import { useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Download, FileUp, Loader2 } from 'lucide-react'
import { Drawer } from '@/components/ui/Drawer'
import { Button } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { testSetsApi, testSetsQueryKey } from '../api'
import { extractApiError, type NormalizedError } from '@/lib/api'

interface Props {
  open: boolean
  onClose: () => void
}

export function UploadTestSetDrawer({ open, onClose }: Props) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [name, setName] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState<NormalizedError | null>(null)

  function reset() {
    setName('')
    setFile(null)
    setError(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const mutation = useMutation({
    mutationFn: () => {
      if (!file) throw new Error('Selecione um arquivo')
      return testSetsApi.upload({ name: name.trim() || file.name, file })
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: testSetsQueryKey.list() })
      reset()
      onClose()
      navigate(`/benchmarks/${data.testSet.id}`)
    },
    onError: (err) => setError(extractApiError(err)),
  })

  return (
    <Drawer
      open={open}
      onClose={() => {
        if (mutation.isPending) return
        reset()
        onClose()
      }}
      title="Importar base de teste"
      description="Planilha .xlsx ou .csv com as colunas ID_EXTERNO, PROCESSO, TEXTO, CLASSE."
      width={560}
      closeOnOverlay={!mutation.isPending}
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            onClick={() => {
              reset()
              onClose()
            }}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending} disabled={!file}>
            <FileUp className="h-4 w-4" />
            Importar
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        <Field
          label="Nome da base"
          htmlFor="ts-name"
          hint="Opcional — usa o nome do arquivo se vazio."
        >
          <Input
            id="ts-name"
            placeholder="Ex: Base validada Maio 2026"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>

        <Field label="Arquivo" required hint="xlsx ou csv, máx 25 MB">
          <div className="flex flex-col gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="block text-sm text-fg-2 file:mr-3 file:px-3 file:h-9 file:rounded-md file:border-0 file:bg-brand-50 file:text-brand-700 file:text-sm file:font-medium file:cursor-pointer hover:file:bg-brand-100"
            />
            {file && (
              <div className="text-xs text-fg-3">
                <span className="data-mono">{file.name}</span> · {(file.size / 1024).toFixed(1)} KB
              </div>
            )}
          </div>
        </Field>

        <a
          href={testSetsApi.sampleCsvUrl()}
          download="sophia-test-set-sample.csv"
          className="inline-flex items-center gap-1.5 text-sm text-brand-700 hover:text-brand-800 underline w-fit"
        >
          <Download className="h-3.5 w-3.5" />
          Baixar planilha modelo
        </a>

        {error && (
          <div className="text-sm text-danger bg-danger-bg border border-danger/30 rounded-md px-3 py-2">
            {error.message}
          </div>
        )}

        {mutation.isPending && (
          <div className="text-sm text-fg-3 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Importando e resolvendo classes…
          </div>
        )}
      </div>
    </Drawer>
  )
}
